import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import InfoOnline from "../../components/InfoOnline";
import NotPermissionComponent from "../../dashboard/NotPermissionComponent";
import {useTaxPayerStorage} from "../../../services/storage/TaxPayerStorage";
import {
    useFindLegalComplianceCheck,
    useFindPDFLegalComplianceCheck
} from "../../../services/apis/apiRuc";
import {LegalComplianceCheckInterface} from "../../../services/interface/query/legal-compliance-check-interface";
import {currentDateEcuador, formatDateString} from "../../../utils/daysOptions";
import {RiFundsLine, RiPrinterLine} from "react-icons/ri";

const Field = ({
                   label, value, bold = false,
               }: {
    label: string;
    value: string;
    bold?: boolean;
}) => (
    <div className="flex flex-col">
        <span className="text-sm text-gray-600 font-medium">{label}:</span>
        <span className={`text-sm ${bold ? "font-bold text-blue-800" : "text-gray-800"}`}> {value}</span>
    </div>
);


export default function LegalComplianceCheck() {
    const {t} = useTranslation("global") as { t: (key: string) => string };
    const person = useTaxPayerStorage((data) => data.data);
    const [response, setResponse] = useState<LegalComplianceCheckInterface>();

    const {
        mutate: getLegalInfo, data: successData, isPending: loading, error,
    } = useFindLegalComplianceCheck();

    const {mutate: downloadLegalCompliance, isPending: downloading} = useFindPDFLegalComplianceCheck();

    useEffect(() => {
        if (person?.taxIdentificationNumber || person?.accountant) {
            getLegalInfo({
                identifier: person.taxIdentificationNumber || person.accountant,
                msj: t("legal.title"),
            });
        }
    }, [person]);

    useEffect(() => {
        if (successData) {
            setResponse(successData);
        }
    }, [successData]);

    return (
        <div className="flex flex-col gap-4">
            <div className={'flex flex-row gap-2'}>
                <h3>
                    {t("legal-compliance-title")}
                </h3>
                <InfoOnline
                    onClick={() => {
                        getLegalInfo({
                            identifier: person?.taxIdentificationNumber || person?.accountant,
                            msj: t("legal.title"),
                        });
                    }}
                />
            </div>
            <NotPermissionComponent
                title="/pj_information/legalComplianceCheck"
                loading={loading}
                error={error}
                className="min-h-[100px]"
                component={
                    response ? (
                        <div
                            className="max-w-4xl w-full border border-gray-200 shadow-sm bg-white p-6 rounded-lg mx-auto">
                            <h2 className="text-center text-lg font-semibold text-gray-900 mb-6 flex items-center justify-center gap-2">
                                {person?.businessName}
                                <RiFundsLine className={'text-[20px] text-green-500'}/>
                            </h2>
                            <div
                                className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 border-t border-b py-6 px-2">
                                <Field label={t("ruc")} value={response.taxIdNumber ? response.taxIdNumber : '-'}/>
                                <Field label={t("caseFile")} value={`$${response.caseFile ? response.caseFile : '-'}`}/>
                                <Field label={t("legalRepresentative")} value={response.legalRepresentative ? response.legalRepresentative : '-'}/>
                                <Field label={t("shareCapital")} value={`$${response.shareCapital ? response.shareCapital : '-'}`}/>
                                <Field label={t("legalStatus")} value={response.legalStatus ? response.legalStatus : '-'}/>
                                <Field
                                    label={t("obligation")}
                                    value={response.obligation ? response.obligation : '-'}
                                    bold
                                />
                            </div>
                            <div className="flex justify-center mt-4">
                                <button
                                    onClick={() =>
                                        downloadLegalCompliance({
                                            identifier: person?.taxIdentificationNumber || person?.accountant,
                                            msj: t("legal.title"),
                                        })
                                    }
                                    disabled={downloading || !response}
                                    className={`flex items-center gap-2 ${
                                        downloading ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-blue-100 hover:bg-blue-200 text-blue-800"
                                    } text-sm font-medium py-1.5 px-3 rounded-md shadow-sm border border-blue-200`}
                                >
                                    <RiPrinterLine className={'text-[17px]'}/>
                                    {downloading ? t("downloading") : t("printCertificate")}
                                </button>
                            </div>
                            <p className="text-xs text-center mt-2 text-gray-500">
                                {t("infoLegalComplianceCheck")} {formatDateString(currentDateEcuador())}
                            </p>
                        </div>
                    ) : (
                        <div className="text-center text-sm text-gray-500">{t("no-records")}</div>
                    )
                }
            />
        </div>
    );
}
